import { ChevronDown, ChevronUp } from "lucide-react";
import React, { useState, useRef, useEffect } from "react";
import { Navbar, Nav, Container, NavDropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  manageData,
  importData,
  customerPresentations,
  preSalesTools,
  businessAnalytics,
  licReports,
  licCommReports,
  specialPlans,
  multiInsurer,
  backOffice,
  newDataEntry,
  licPresentation,
  withdrawnPlans,
  adminMenuItems,
  generalMenuItems,
  commonMenuItems,
  generalSections,
  otherInvestmentSections,
  commonSections,
  adminSections,
} from "./menuData";

const DashboardHeader = () => {
  const navigate = useNavigate();

  const [showLife, setShowLife] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showGeneral, setShowGeneral] = useState(false);
  const [showOther, setShowOther] = useState(false);
  const [showCommon, setShowCommon] = useState(false);

  // Under construction modal state
  const [underModal, setUnderModal] = useState({ visible: false, title: '', img: '' });
  const closeUnderModal = () => setUnderModal({ visible: false, title: '', img: '' });

  // Dropdown section toggle states
  const [openSections, setOpenSections] = useState({
    manageData: true,
    importData: true,
    customerPresentations: true,
    preSalesTools: true,
    businessAnalytics: true,
    licReports: true,
    licCommReports: true,
    specialPlan: true,
    multiInsurer: true,
    backOffice: true,
    newDataEntry: true,
    licPresentation: true,
    withdrawnPlans: true,
  });

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // close all dropdowns
  const closeAllDropdowns = () => {
    setShowLife(false);
    setShowAdmin(false);
    setShowGeneral(false);
    setShowOther(false);
    setShowCommon(false);
  };

  // toggle a single dropdown and ensure others are closed
  const toggleDropdown = (name) => {
    const current = (name === 'life') ? showLife : (name === 'admin') ? showAdmin : (name === 'general') ? showGeneral : (name === 'other') ? showOther : (name === 'common') ? showCommon : false;
    closeAllDropdowns();
    if (!current) {
      if (name === 'life') setShowLife(true);
      if (name === 'admin') setShowAdmin(true);
      if (name === 'general') setShowGeneral(true);
      if (name === 'other') setShowOther(true);
      if (name === 'common') setShowCommon(true);
    }
  };

  // build life insurance sections array (ordered)
  const lifeSections = [
    { title: 'Manage Data', items: manageData },
    { title: 'Import Data', items: importData },
    { title: 'Customer Presentations', items: customerPresentations },
    { title: 'Pre-Sales Tools', items: preSalesTools },
    { title: 'Business Analytics', items: businessAnalytics },
    { title: 'LIC Reports', items: licReports },
    { title: 'LIC Comm. Reports', items: licCommReports },
    { title: 'Multi-Insurer Reports', items: multiInsurer },
    { title: 'Back Office Reports', items: backOffice },
    { title: 'New Data Entry', items: newDataEntry },
    { title: 'LIC Presentation', items: licPresentation },
    { title: 'Withdrawn Plans', items: withdrawnPlans },
    { title: 'Special Plan Presentation', items: specialPlans }
  ];

  const [selectedTab, setSelectedTab] = useState("");
  const lifeRef = useRef(null);
  const adminRef = useRef(null);
  const generalRef = useRef(null);
  const otherRef = useRef(null);
  const commonRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (lifeRef.current && !lifeRef.current.contains(event.target)) setShowLife(false);
      if (adminRef.current && !adminRef.current.contains(event.target)) setShowAdmin(false);
      if (generalRef.current && !generalRef.current.contains(event.target)) setShowGeneral(false);
      if (otherRef.current && !otherRef.current.contains(event.target)) setShowOther(false);
      if (commonRef.current && !commonRef.current.contains(event.target)) setShowCommon(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 🔹 Reusable Section Component
  const Section = ({ title, open, toggle, items }) => {
    const allComingSoon = items.every((it) => !it.path);
    return (
      <div style={{ margin: 0, display: 'inline-block', width: '100%', breakInside: 'avoid', WebkitColumnBreakInside: 'avoid' }}>
        <div
          className="dropdown-section-header d-flex justify-content-between align-items-center"
          style={{ cursor: "pointer", whiteSpace: 'nowrap', gap: 8, marginBottom: 6 }}
          onClick={toggle}
        >
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
            {allComingSoon && (
              <span style={{ color: "#d9534f", fontSize: 12, marginLeft: 6 }}>
                {/* (Coming Soon) */}
              </span>
            )}
          </div>
          <div style={{ flex: '0 0 auto' }}>{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</div>
        </div>
        {open && (
          <div style={{ margin: 0, paddingTop: 0 }}>
            {items.map((it, i) => (
              <NavDropdown.Item
                key={i}
                onClick={() => {
                  if (it.path) {
                    navigate(it.path);
                    closeAllDropdowns();
                    return;
                  }
                  // show under-construction modal for items without a path
                  setUnderModal({ visible: true, title: it.label, img: it.underImage || '/images/UnderConstructionImg.jpg' });
                  closeAllDropdowns();
                }}
                style={{ cursor: it.path ? "pointer" : "pointer", opacity: it.path ? 1 : 0.9, paddingTop: 4, paddingBottom: 4 }}
              >
                {it.label}
              </NavDropdown.Item>
            ))}
          </div>
        )}
      </div>
    );
  };

  // helper: render an array of sections into N vertical columns (fills top-to-bottom)
  const renderColumns = (sectionsArr = [], cols = 3, columnStyle = {}) => {
    if (!Array.isArray(sectionsArr) || sectionsArr.length === 0) return null;
    const per = Math.ceil(sectionsArr.length / cols);
    const columns = [];
    for (let c = 0; c < cols; c++) {
      const start = c * per;
      const chunk = sectionsArr.slice(start, start + per);
      columns.push(
        <div key={c} style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '33%', boxSizing: 'border-box', ...columnStyle }}>
          {chunk.map((sec, idx) => (
            <Section
              key={sec.title || idx}
              title={sec.title}
              open={openSections[sec.title ? sec.title.replace(/\s+/g, '') : idx] ?? true}
              toggle={() => toggleSection(sec.title ? sec.title.replace(/\s+/g, '') : idx)}
              items={sec.items || []}
            />
          ))}
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', gap: '1rem', width: '100%' }}>
        {columns}
      </div>
    );
  };

  // Menu data imported from menuData.js

  // Admin/general/common menu data imported from menuData.js

  // 🔹 RENDER NAVBAR
  return (
    <Navbar expand="lg" bg="white" variant="light" className="shadow-sm border-bottom py-2">
      <Container>
            <Navbar.Brand onClick={() => { navigate("/dashboard"); closeAllDropdowns(); }} style={{ cursor: "pointer" }}>
          {/* <img src="/images/Isure_Logo.jpg" alt="ISure Logo" style={{ height: "75px" }} /> */}
          <img src="/images/final_Logo_Image.jpeg" alt="ISure Logo" style={{ height: "8rem", width: '100%' }} />
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="navbar-nav" />
        <Navbar.Collapse id="navbar-nav">
          <Nav className="mx-auto d-flex flex-wrap gap-3 justify-content-center">
            {/* LIFE INSURANCE MENU (first) */}
            <NavDropdown
              title={
                <div className="menu-item" onClick={() => { toggleDropdown('life'); setSelectedTab("life"); }}>
                  <img src="images/AdminHeaderImages/LifeInsurance.png" alt="Life" />
                  <span>Life Insurance</span>
                </div>
              }
              show={showLife}
              ref={lifeRef}
              className="hide-caret-dropdown nav-item"
              id="life-dropdown"
            >
              <div
                className="px-3 py-2"
                style={{ display: 'flex', gap: '1rem', width: '1000px', maxWidth: '1200px', maxHeight: '80vh', overflowY: 'auto' }}
              >
                {renderColumns(lifeSections, 3, { paddingRight: 6 })}
              </div>
            </NavDropdown>

            {/* GENERAL INSURANCE MENU (second) */}
            <NavDropdown
              title={
                <div className="menu-item" onClick={() => { toggleDropdown('general'); setSelectedTab("general"); }}>
                  <img src="images/AdminHeaderImages/GeneralInsurance.png" alt="General" />
                  <span>General Insurance</span>
                </div>
              }
              show={showGeneral}
              ref={generalRef}
              className="hide-caret-dropdown nav-item"
            >
              <div className="px-3 py-2" style={{ width: '100%', maxWidth: '1000px', maxHeight: '80vh', overflowY: 'auto' }}>
                {renderColumns(generalSections, 3)}
              </div>
            </NavDropdown>

            {/* Other Investments (third) */}
            <NavDropdown
              title={
                <div className="menu-item" onClick={() => { toggleDropdown('other'); setSelectedTab('other'); }}>
                  <img src="images/AdminHeaderImages/investment.png" alt="Other Investments" />
                  <span>Other Investments</span>
                </div>
              }
              show={showOther}
              ref={otherRef}
              className="hide-caret-dropdown nav-item"

            >
              <div className="px-3 py-2" style={{ width: '100%', maxWidth: '1000px', maxHeight: '80vh', overflowY: 'auto' }}>
                {renderColumns(otherInvestmentSections, 3)}
              </div>
            </NavDropdown>

            {/* COMMON MENU (fourth) */}
            <NavDropdown
              title={
                <div className="menu-item" onClick={() => { toggleDropdown('common'); setSelectedTab("common"); }}>
                  <img src="images/AdminHeaderImages/Common.png" alt="Common" />
                  <span>Common</span>
                </div>
              }
              show={showCommon}
              ref={commonRef}
              className="hide-caret-dropdown nav-item center-dropdown"

            >
              <div
                className="px-3 py-2"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(220px, 1fr))",
                  gap: "0.5rem 1rem",
                  alignItems: "start",
                  justifyContent: "start",
                  width: "100%",
                  maxWidth: "1000px",
                }}
              >
                {commonSections.map((sec, idx) => (
                  <Section key={idx} title={sec.title} open={openSections[sec.title.replace(/\s+/g, '')] ?? true} toggle={() => toggleSection(sec.title.replace(/\s+/g, ''))} items={sec.items} />
                ))}
              </div>
            </NavDropdown>

            {/* ADMIN MENU (fifth) */}
            <NavDropdown
              title={
                <div className="menu-item" onClick={() => { toggleDropdown('admin'); setSelectedTab("admin"); }}>
                  <img src="images/AdminHeaderImages/Admin.png" alt="Admin" />
                  <span>Admin</span>
                </div>
              }
              show={showAdmin}
              ref={adminRef}
              className="hide-caret-dropdown nav-item center-dropdown"

            >
              <div
                className="px-3 py-2"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(220px, 1fr))",
                  gap: "0.5rem 1rem",
                  alignItems: "start",
                  justifyContent: "start",
                  width: "100%",
                  maxWidth: "1000px",
                }}
              >
                {adminSections.map((sec, idx) => (
                  <Section key={idx} title={sec.title} open={openSections[sec.title.replace(/\s+/g, '')] ?? true} toggle={() => toggleSection(sec.title.replace(/\s+/g, ''))} items={sec.items} />
                ))}
              </div>
            </NavDropdown>

            {/* My Menu (sixth) */}
            <Nav.Link href="#" className={`menu-item nav-item${selectedTab === 'myMenu' ? ' selected-Tab' : ''}`} onClick={() => { setSelectedTab('myMenu'); closeAllDropdowns(); }}>
              <img src="images/AdminHeaderImages/Menu.png" alt="My Menu" />
              <span>My Menu</span>
            </Nav.Link>

            {/* Profile (seventh) */}
            <NavDropdown
              title={
                <div className="menu-item" onClick={() => setSelectedTab('profile')}>
                  <img
                    src="/images/user_image.png"
                    alt="Profile"
                    className="rounded-circle border"
                    style={{ width: "35px", height: "35px" }}
                  />
                  <span className="fw-bold">ISure</span>
                </div>
              }
              id="profile-dropdown"
              align="end"
              className="hide-caret-dropdown nav-item"

            >
              <NavDropdown.Item onClick={() => navigate('/raise-ticket')}>Raise a Ticket</NavDropdown.Item>
              <NavDropdown.Item onClick={() => navigate('/my-profile')}>Edit Profile</NavDropdown.Item>
              <NavDropdown.Item>My Tickets</NavDropdown.Item>
              <NavDropdown.Item>Video Tutorials</NavDropdown.Item>
              <NavDropdown.Item>Speed Test</NavDropdown.Item>
              <NavDropdown.Item>Search Menu</NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item
                onClick={() => navigate('/')}
                className="text-center text-white bg-danger py-2 rounded"
              >
                Sign Out
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Navbar.Collapse>
        {/* Under-construction modal (simple inline overlay) */}
        {underModal.visible && (
          <div
            onClick={closeUnderModal}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ position: 'relative', background: '#fff', padding: 20, borderRadius: 8, maxWidth: '90%', maxHeight: '90%', textAlign: 'center' }}
            >
              <button
                onClick={closeUnderModal}
                aria-label="Close"
                title="Close"
                style={{
                  position: 'absolute',
                  right: 12,
                  top: 12,
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '50%',
                  width: 36,
                  height: 36,
                  fontSize: 20,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                }}
              >
                ×
              </button>
              <h5 style={{ marginTop: 6 }}>{underModal.title} <small style={{ color: '#d9534f' }}>— Coming Soon</small></h5>
              <div style={{ marginTop: 12 }}>
                <img src={underModal.img} alt="Under Construction" style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain' }} />
              </div>
            </div>
          </div>
        )}
      </Container>
    </Navbar>
  );
};

export default DashboardHeader;
